import { useEffect, useCallback } from 'react';
import { useSignalR } from './useSignalR';
import { useGameStore } from '../stores/gameStore';
import { useUIStore } from '../stores/uiStore';
import { signalRService } from '../services/signalRClient';

export function useGameRoom(roomId: string | undefined, gameType: string | undefined, vsAI?: boolean) {
  const { isConnecting, error, joinRoomById, leaveRoom, invoke } = useSignalR();
  const { phase, currentTurnPlayerId, mySessionId } = useGameStore();
  const { addToast } = useUIStore();

  const isMyTurn = currentTurnPlayerId === mySessionId;

  useEffect(() => {
    if (roomId && gameType && !signalRService.roomId) {
      joinRoomById(roomId).catch((err) => {
        addToast(err.message || 'Failed to join game', 'error');
      });
    }
  }, [roomId, gameType, vsAI, joinRoomById, addToast]);

  const setReady = useCallback(
    (ready: boolean) => {
      invoke('SetReady', ready);
    },
    [invoke]
  );

  const playCard = useCallback(
    (cardId: string) => {
      invoke('PlayCard', cardId);
    },
    [invoke]
  );

  const submitBid = useCallback(
    (bid: number, options?: { isBlind?: boolean; isNil?: boolean }) => {
      invoke('SubmitBid', bid, options?.isBlind ?? false, options?.isNil ?? false);
    },
    [invoke]
  );

  const passBid = useCallback(() => {
    invoke('PassBid');
  }, [invoke]);

  const placeDomino = useCallback(
    (dominoId: string, arm: string) => {
      invoke('PlaceDomino', dominoId, arm);
    },
    [invoke]
  );

  const drawCard = useCallback(
    (source: 'deck' | 'discard') => {
      invoke('DrawCard', source);
    },
    [invoke]
  );

  const discardCard = useCallback(
    (cardId: string) => {
      invoke('DiscardCard', cardId);
    },
    [invoke]
  );

  const spread = useCallback(
    (cardIds: string[]) => {
      invoke('Spread', cardIds);
    },
    [invoke]
  );

  const knock = useCallback(() => {
    invoke('Knock');
  }, [invoke]);

  const drop = useCallback(() => {
    invoke('Drop');
  }, [invoke]);

  const nextRound = useCallback(() => {
    invoke('NextRound');
  }, [invoke]);

  const sendChat = useCallback(
    (message: string) => {
      invoke('SendChat', message);
    },
    [invoke]
  );

  return {
    isConnecting,
    error,
    phase,
    isMyTurn,
    setReady,
    playCard,
    submitBid,
    passBid,
    placeDomino,
    drawCard,
    discardCard,
    spread,
    knock,
    drop,
    nextRound,
    sendChat,
    leaveRoom,
  };
}
